import produce from 'immer';
import {useSelector} from 'react-redux';

import {CustomField, Strand} from 'constants/MolbioConstants';
import {defaultPastelColor, randomPastelColor} from 'lib/Colors';
import {BenchlingAction} from 'redux-stores/Action';
import {BenchlingState} from 'redux-stores/reduceApp';
import {omit} from 'util/omitKey';

import {FeaturePanelUIActionType} from './FeaturePanelUIReduxActions';

/**
 * State of the create and edit form for annotations and translations
 * in the feature panel for each open sequence tab
 */
export interface AllFeaturePanelUIState {
  [tabId: string]: FeaturePanelUIState;
}

export interface FeaturePanelUIState {
  selectionStart: number | null;
  selectionEnd: number | null;
  annotationEditData: AnnotationPanelEditData;
  translationPanelState: TranslationPanelUIState;
}

export interface TranslationPanelUIState {
  // Whether the create new translation form is active and visible in the panel
  isCreateActive: boolean;
  initialStrand: Strand;
}

// The current state of all annotation panel edit/create form input fields
export interface AnnotationPanelEditData {
  // Whether the create annotation form is active and visible in the panel
  isCreateActive: boolean;
  name: string;
  annotationType: string;
  start: number | null;
  end: number | null;
  color: string;
  strand: number;
  notes: string;
  customFields: Array<CustomField>;
  // Whether to focus the name field
  shouldFocus: boolean;
}

export const DEFAULT_ANNOTATION_PANEL_EDIT_DATA = {
  isCreateActive: false,
  name: '',
  annotationType: '',
  start: null,
  end: null,
  color: defaultPastelColor,
  strand: 1,
  notes: '',
  customFields: [],
  shouldFocus: false,
};

const DEFAULT_TRANSLATION_PANEL_STATE = {
  isCreateActive: false,
  initialStrand: 1 as Strand,
};

const ALL_FEATURE_PANEL_UI_INITIAL_STATE = {};

export const FEATURE_PANEL_UI_INITIAL_STATE = {
  selectionStart: 0,
  selectionEnd: null,
  annotationEditData: DEFAULT_ANNOTATION_PANEL_EDIT_DATA,
  translationPanelState: DEFAULT_TRANSLATION_PANEL_STATE,
};

function reduceFeaturePanelUIByTab(
  state: FeaturePanelUIState = FEATURE_PANEL_UI_INITIAL_STATE,
  action: BenchlingAction
): FeaturePanelUIState {
  switch (action.type) {
    case FeaturePanelUIActionType.CHANGE_SELECTION_RANGE: {
      // Update the edit form values to match the sequence selection if creating a new annotation
      return produce(state, (draftState) => {
        draftState.selectionStart = action.start;
        draftState.selectionEnd = action.end;
        if (state.annotationEditData.isCreateActive) {
          draftState.annotationEditData.start = action.start;
          draftState.annotationEditData.end = action.end;
        }
      });
    }
    case FeaturePanelUIActionType.SELECT_ANNOTATION: {
      return produce(state, (draftState) => {
        draftState.annotationEditData = action.data;
      });
    }
    case FeaturePanelUIActionType.UPDATE_ANNOTATION_EDIT_DATA:
      return produce(state, (draftState) => {
        draftState.annotationEditData = {
          ...state.annotationEditData,
          ...action.data,
        };
      });
    case FeaturePanelUIActionType.OPEN_CREATE_NEW_ANNOTATION:
      return produce(state, (draftState) => {
        draftState.annotationEditData = {
          ...DEFAULT_ANNOTATION_PANEL_EDIT_DATA,
          isCreateActive: true,
          start: state.selectionStart,
          end: state.selectionEnd,
          color: randomPastelColor(),
          shouldFocus: true,
        };
      });
    case FeaturePanelUIActionType.CANCEL_CREATE_NEW_ANNOTATION:
      return produce(state, (draftState) => {
        draftState.annotationEditData.isCreateActive = false;
        draftState.annotationEditData.shouldFocus = false;
      });
    case FeaturePanelUIActionType.FOCUS_ANNOTATION_FORM_COMPLETE: {
      return produce(state, (draftState) => {
        draftState.annotationEditData.shouldFocus = false;
      });
    }
    case FeaturePanelUIActionType.OPEN_CREATE_NEW_TRANSLATION:
      return produce(state, (draftState) => {
        draftState.translationPanelState.isCreateActive = true;
        draftState.translationPanelState.initialStrand = action.strand;
      });
    case FeaturePanelUIActionType.CANCEL_CREATE_NEW_TRANSLATION:
      return produce(state, (draftState) => {
        draftState.translationPanelState.isCreateActive = false;
        draftState.translationPanelState.initialStrand = DEFAULT_TRANSLATION_PANEL_STATE.initialStrand;
      });
    default:
      return state;
  }
}

export default function reduceFeaturePanelUI(
  state: AllFeaturePanelUIState = ALL_FEATURE_PANEL_UI_INITIAL_STATE,
  action: BenchlingAction
): AllFeaturePanelUIState {
  switch (action.type) {
    case FeaturePanelUIActionType.CLOSE_SEQUENCE:
      return omit(state, action.sequenceId);
    case FeaturePanelUIActionType.CHANGE_SELECTION_RANGE:
    case FeaturePanelUIActionType.SELECT_ANNOTATION:
    case FeaturePanelUIActionType.UPDATE_ANNOTATION_EDIT_DATA:
    case FeaturePanelUIActionType.OPEN_CREATE_NEW_ANNOTATION:
    case FeaturePanelUIActionType.CANCEL_CREATE_NEW_ANNOTATION:
    case FeaturePanelUIActionType.FOCUS_ANNOTATION_FORM_COMPLETE:
    case FeaturePanelUIActionType.OPEN_CREATE_NEW_TRANSLATION:
    case FeaturePanelUIActionType.CANCEL_CREATE_NEW_TRANSLATION:
      return {
        ...state,
        [action.sequenceId]: reduceFeaturePanelUIByTab(
          state[action.sequenceId] ?? FEATURE_PANEL_UI_INITIAL_STATE,
          action
        ),
      };
    default:
      return state;
  }
}

export function useAnnotationPanelUIBySequence(sequenceId: string): AnnotationPanelEditData {
  return useSelector(
    (state: BenchlingState) =>
      state.ui.molbio.featurePanelUI[sequenceId]?.annotationEditData ?? DEFAULT_ANNOTATION_PANEL_EDIT_DATA
  );
}

export function useFeaturePanelStateBySequence(sequenceId: string): FeaturePanelUIState {
  return useSelector(
    (state: BenchlingState) => state.ui.molbio.featurePanelUI[sequenceId] ?? FEATURE_PANEL_UI_INITIAL_STATE
  );
}
